import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';

// Create Context
const AuthContext = createContext();

// Provider Component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null if logged out, user object if logged in
    const [isLoading, setIsLoading] = useState(true); // Check session on initial load
    const [error, setError] = useState(''); // Store login/logout errors
    const [consentGiven, setConsentGiven] = useState(null); // null = undecided, true = accepted, false = rejected

    // Function to check session on initial load or refresh
    const checkUserSession = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get('/api/auth/session');
            if (response.data.success) {
                if (response.data.isAuthenticated) {
                    setUser(response.data.user);
                } else {
                    setUser(null);
                }
                // Update consent status regardless of authentication
                setConsentGiven(response.data.consentGiven); // Will be true, false, or null
            } else {
                // Handle potential API error structure where success=false
                setUser(null);
                setConsentGiven(null); // Assume undecided on API error
                console.error("Session check API returned success:false");
            }
        } catch (err) {
            console.error("Session check failed:", err);
            setUser(null); // Assume logged out on error
            setConsentGiven(null); // Assume undecided on network/server error
            // Optionally set an error message here if needed
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Check session on initial mount
    useEffect(() => {
        checkUserSession();
    }, [checkUserSession]);

    // Login function
    const login = async (username, password) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post('/api/auth/login', { username, password });
            if (response.data.success) {
                setUser(response.data.user);
                setIsLoading(false);
                return true; // Indicate success
            } else {
                // Should not happen with proper status codes, but handle just in case
                setError(response.data.message || 'Login failed.');
                setIsLoading(false);
                return false;
            }
        } catch (err) {
            console.error("Login error:", err);
            setError(err.response?.data?.message || 'Login failed. Please check credentials or server status.');
            setUser(null);
            setIsLoading(false);
            return false; // Indicate failure
        }
    };

    // Logout function
    const logout = async () => {
        setIsLoading(true);
        setError('');
        try {
            await axios.post('/api/auth/logout');
            setUser(null);
        } catch (err) {
            console.error("Logout error:", err);
            setError(err.response?.data?.message || 'Logout failed.');
            // Even if logout API fails, clear user state on frontend
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Submit Consent Decision
    const submitConsentDecision = async (choice) => {
        // Don't set loading state here, banner will just disappear
        setError('');
        try {
            const response = await axios.post('/api/consent', { choice });
            if (response.data.success) {
                setConsentGiven(choice === 'accept'); // Only dismiss after durable server acceptance.
                return true;
            } else {
                // Handle potential API error structure
                setError(response.data.message || 'Failed to save consent preference.');
                return false;
            }
        } catch (err) {
            console.error("Consent submission error:", err);
            setError(err.response?.data?.message || 'Failed to save consent preference.');
            // Keep consent state as null, banner should remain visible
            return false;
        }
    };

    // Value provided to consuming components
    const value = {
        user,
        isAuthenticated: !!user, // Boolean flag derived from user state
        isLoading,
        error,
        consentGiven, // Expose consent state
        login,
        logout,
        checkUserSession, // Expose if needed for manual refresh
        submitConsentDecision // Expose consent function
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the Auth Context
export const useAuth = () => {
    return useContext(AuthContext);
};
